import React, { useState } from 'react';
import { Alert, Box, Button, Chip, Stack, Typography } from '@mui/material';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';

const API_PATH = '/api/ai-developer/v1/chat/completions';
const API_BASE = 'https://api.unifloo.com';
const MODEL = 'REPLACE_WITH_MODEL_NAME';

const curlExample = `export UNIFLOO_API_KEY='uf_live_REPLACE_WITH_YOUR_KEY'\ncurl -X POST '${API_BASE}${API_PATH}' \\\n  -H "Authorization: Bearer $UNIFLOO_API_KEY" \\\n  -H 'Content-Type: application/json' \\\n  -d '{"model":"${MODEL}","messages":[{"role":"user","content":"Explain neural networks simply."}],"max_tokens":300}'`;

const bodyExample = `{
  "model": "${MODEL}",
  "messages": [
    { "role": "user", "content": "Explain neural networks simply." }
  ],
  "max_tokens": 300
}`;

const pythonExample = `import os
import requests

url = '${API_BASE}${API_PATH}'
response = requests.post(url, headers={
    'Authorization': f"Bearer {os.environ['UNIFLOO_API_KEY']}",
    'Content-Type': 'application/json',
}, json={
    'model': '${MODEL}',
    'messages': [{'role': 'user', 'content': 'Create a study plan.'}],
    'max_tokens': 500,
}, timeout=120)
response.raise_for_status()
print(response.json()['choices'][0]['message']['content'])`;

const nodeExample = `const response = await fetch('${API_BASE}${API_PATH}', {
  method: 'POST',
  headers: {
    Authorization: \`Bearer \${process.env.UNIFLOO_API_KEY}\`,
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    model: '${MODEL}',
    messages: [{ role: 'user', content: 'Hello!' }]
  })
});
if (!response.ok) throw new Error(\`AI API failed: \${response.status}\`);
const data = await response.json();
console.log(data.choices[0].message.content);`;

const errorCodes = [
  ['400', 'Invalid request, unsupported upstream provider, or oversized messages.'],
  ['401', 'Missing, invalid, expired, or revoked API key.'],
  ['403', 'Requested model has not been approved for this application.'],
  ['429', 'Request quota or concurrency limit exceeded.'],
  ['502', 'Upstream AI server failed or timed out.'],
  ['503', 'Provider host is not permitted.'],
];

function DocPanel({ title, children }) {
  return (
    <Box sx={{ border: '1px solid #CFE1F9', bgcolor: '#F6FAFF', borderRadius: 2.5, p: { xs: 1.5, md: 2 }, mb: 2 }}>
      <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1, color: '#1F2937' }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function HighlightedCode({ code }) {
  // Highlight only placeholder values; copying always preserves the original plain text.
  const pattern = /(uf_live_REPLACE_WITH_YOUR_KEY|REPLACE_WITH_MODEL_NAME|UNIFLOO_API_KEY)/g;
  return code.split(pattern).map((part, index) =>
    /^(uf_live_REPLACE_WITH_YOUR_KEY|REPLACE_WITH_MODEL_NAME|UNIFLOO_API_KEY)$/.test(part) ? (
      <Box component="span" key={index} sx={{ color: '#FFAA55', bgcolor: 'rgba(240,120,40,0.18)', fontWeight: 800, borderRadius: '3px' }}>{part}</Box>
    ) : part
  );
}

function CodeBlock({ code, label }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };
  return (
    <Box sx={{ position: 'relative', mt: 1 }}>
      <Box sx={{ bgcolor: '#10182D', borderRadius: 2.5, overflow: 'hidden' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1.5, pt: 1 }}>
          <Typography variant="caption" sx={{ color: '#9EAFCA' }}>{label}</Typography>
          <Button size="small" onClick={copy} startIcon={<ContentCopyOutlinedIcon fontSize="small" />}
            sx={{ color: '#D8E6FF', textTransform: 'none' }}>
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </Stack>
        <Box component="pre" sx={{ m: 0, px: 2, pb: 2, pt: 0.5, color: '#E1E7F1', fontSize: 13,
          lineHeight: 1.7, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
          overflowX: 'auto', whiteSpace: 'pre', maxWidth: '100%' }}>
          <HighlightedCode code={code} />
        </Box>
      </Box>
    </Box>
  );
}

export default function AIDeveloperDocumentation() {
  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h6" fontWeight={800} sx={{ color: '#1764C5', mb: 1 }}>
        AI Developer API — Integration Guide
      </Typography>
      <Typography sx={{ mb: 2 }}>
        Use your approved UniFLOO API key to connect a student project, faculty application, or college lab
        to the AI models assigned by your campus administrator.
      </Typography>

      <DocPanel title="1. Request API Access">
        <Typography>Open the <strong>Request Access</strong> tab, choose Student, Faculty, or Lab,
          enter your project name and purpose, and submit your application. For lab projects, include
          the faculty supervisor.</Typography>
      </DocPanel>

      <DocPanel title="2. Administrator Approval and Model Grants">
        <Typography>Your campus administrator reviews requests in <strong>/AIDeveloperAdmin</strong>.
          Approval alone is not sufficient: an administrator must also assign at least one model,
          its public alias, and its daily request and monthly token quotas. One project can receive
          multiple model grants, potentially using different upstream provider keys.</Typography>
      </DocPanel>

      <DocPanel title="3. Generate Your API Key">
        <Typography>After approval and model assignment, open <strong>API Keys</strong>, choose your
          approved application, load its model grants, and select <strong>Generate API Key</strong>.
          Copy the key immediately: only its hash is stored, so it cannot be displayed again.
          Keys begin with <Box component="code">uf_live_</Box>, expire after 180 days, and can be revoked.</Typography>
      </DocPanel>

      <DocPanel title="4. How to Call the UniFLOO AI API">
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} sx={{ mb: 1 }}>
          <Chip label="POST" color="success" size="small" sx={{ alignSelf: 'flex-start', fontWeight: 800 }} />
          <Typography sx={{ fontFamily: 'monospace', overflowWrap: 'anywhere' }}>{API_PATH}</Typography>
        </Stack>
        <Typography>All campuses can use the same central API hostname <Box component="code" sx={{ mx: 0.5, color: '#E46B16', fontWeight: 800 }}>{API_BASE}</Box> once DNS, HTTPS, and backend routing are configured. Campus websites such as kiet.unifloo.com and test.unifloo.com can remain separate. The API identifies the authorized application and campus from its API key; do not use a campus website hostname as the API host unless it also routes this endpoint.</Typography>
        <Alert severity="warning" sx={{ mt: 1 }}>Before testing, confirm that api.unifloo.com resolves to your backend, has a valid TLS certificate, and routes this API path. Documentation alone does not configure DNS or your reverse proxy.</Alert>
      </DocPanel>

      <DocPanel title="How to Call from a 3rd-Party Application — cURL">
        <Typography>Send your API key in the Authorization header and specify an approved public model alias.</Typography>
        <CodeBlock label="bash / cURL" code={curlExample} />
      </DocPanel>

      <DocPanel title="Actual Request Body Example">
        <CodeBlock label="JSON" code={bodyExample} />
      </DocPanel>

      <DocPanel title="Python Integration Example">
        <Typography>Install the requests package if needed and set the UNIFLOO_API_KEY environment variable.</Typography>
        <CodeBlock label="python" code={pythonExample} />
      </DocPanel>

      <DocPanel title="JavaScript (Node.js) Integration Example">
        <Typography>Use this code in a trusted Node.js backend, not in client-side React or a mobile app bundle.</Typography>
        <CodeBlock label="javascript" code={nodeExample} />
      </DocPanel>

      <DocPanel title="Where the Public Model Alias Comes From">
        <Typography>Open <strong>My Applications → View Models</strong> or <strong>API Keys → Load Model Grants</strong>.
          Use the exact <strong>PublicModel</strong> alias assigned by the administrator instead of
          <Box component="code" sx={{ mx: 0.5 }}>{MODEL}</Box> in these examples.
          API keys belong to applications, and the server checks each requested model against the
          application's active grants. For model-specific keys, create separate applications.</Typography>
      </DocPanel>

      <DocPanel title="5. API Error Codes">
        <Stack gap={1}>
          {errorCodes.map(([code, meaning]) => (
            <Stack key={code} direction="row" alignItems="flex-start" gap={1.5}>
              <Chip label={code} size="small" sx={{ fontWeight: 800, minWidth: 54 }} />
              <Typography variant="body2">{meaning}</Typography>
            </Stack>
          ))}
        </Stack>
      </DocPanel>

      <DocPanel title="6. Security Rules">
        <Stack component="ul" sx={{ pl: 3, my: 0 }} gap={1}>
          <li>Never expose API keys in React/browser code, mobile app binaries, public repositories, screenshots, or logs. Call UniFLOO from a backend you control.</li>
          <li>Use separate applications and keys for unrelated student projects.</li>
          <li>Do not send sensitive college or personal data without explicit college authorization.</li>
          <li>Administrators may suspend applications, deactivate model grants, or revoke keys.</li>
          <li>Rotate credentials by revoking old keys and generating replacements.</li>
        </Stack>
      </DocPanel>

      <Alert severity="info" sx={{ border: '1px solid #B8DEFB', borderRadius: 2.5 }}>
        Use the configured API hostname, and replace the API key and public model alias with your actual values.
        The API key must remain secret. The full integration examples are also included in
        <strong> API_KEY_USAGE.md</strong> in this package.
      </Alert>
    </Box>
  );
}
